import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import getEffectivePlanByProfile from "../utils/getEffectivePlanByProfile";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";

const BlockchainCertificate = ({ artwork, profile }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [localProfile, setLocalProfile] = useState(profile || null);
  const profileReady = !!(profile || localProfile);
  const p = profile || localProfile;

const effectivePlan =
  typeof getEffectivePlanByProfile === "function"
    ? getEffectivePlanByProfile(p)
    : String(p?.plan || p?.beta_plan || "standard").trim().toLowerCase();

  const stripeStatusRaw = String(p?.stripe_subscription_status || "").trim().toLowerCase();
  const isDemo = p?.is_demo === true;
  const freeUntil = p?.free_until ? new Date(p.free_until) : null;
  const now = new Date();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Entitlement: subscription active/trialing OR temporary free access OR demo account
  const hasEntitlement =
    isDemo ||
    stripeStatusRaw === "active" ||
    stripeStatusRaw === "trialing" ||
    (freeUntil instanceof Date && !Number.isNaN(freeUntil.getTime()) && freeUntil > now);

  // Plan gating from public.profiles.plan (strict enum via CHECK constraint)
  const hasPaidPlan = ["premium", "art_advisor_premium", "family_office"].includes(effectivePlan);
 
  // Final access: paid plan + entitlement, or demo
  const hasBlockchainAccess = isDemo || (hasPaidPlan && hasEntitlement);

  console.debug("[BlockchainCertificate] access", {
    profileReady,
    is_demo_raw: p?.is_demo,
    effectivePlan,
    stripeStatusRaw,
    freeUntil: freeUntil ? freeUntil.toISOString() : null,
    isDemo,
    hasEntitlement,
    hasPaidPlan,
    hasBlockchainAccess,
  });

  // If profile isn't provided yet, fetch it once (prevents infinite loading)
  useEffect(() => {
    let cancelled = false;
    async function fetchProfileOnce() {
      if (profile) return;
      if (!user?.id) return;
      if (localProfile) return;
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("id, plan, is_demo, stripe_subscription_status, free_until, beta_plan, beta_status")
          .eq("id", user.id)
          .maybeSingle();
        if (error) throw error;
        if (!cancelled) setLocalProfile(data || null);
      } catch (e) {
        console.warn("[BlockchainCertificate] profile fetch failed", e?.message || e);
        if (!cancelled) setLocalProfile(null);
      }
    }
    fetchProfileOnce();
    return () => {
      cancelled = true;
    };
  }, [profile, user?.id, localProfile]);

  const loadCertificate = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { default: blockchainService } = await import('../services/blockchainService');
      const existing = await blockchainService.getLatestArtworkFingerprint(artwork.id);
      setCertificate(existing);
    } catch (err) {
      console.warn("[BlockchainCertificate] fingerprint load failed", err?.message || err);
      setError(t('artworkDetail.proof.loadError', { defaultValue: "Impossible de charger l’empreinte numérique." }));
    } finally {
      setLoading(false);
    }
  }, [artwork?.id, t]);

  useEffect(() => {
    if (artwork?.id) {
      loadCertificate();
    } else {
      setCertificate(null);
    }
  }, [artwork?.id, loadCertificate]);

  const generateCertificate = async () => {
    if (!hasBlockchainAccess) {
      alert(t('artworkDetail.proof.premiumRequired'));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { default: blockchainService } = await import('../services/blockchainService');
      const hashData = await blockchainService.generateCertificateHash(artwork);
      const savedCertificate = await blockchainService.saveCertificate(artwork.id, hashData);

      setCertificate(savedCertificate);
      setLoading(false);

      alert(t('artworkDetail.proof.generateSuccess'));
    } catch (err) {
      setError(t('artworkDetail.proof.generateError'));
      setLoading(false);
    }
  };

  if (!profileReady) {
    return (
      <div style={{
        padding: '2rem',
        border: '1px solid #e5e7eb',
        borderRadius: '0.5rem',
        textAlign: 'center',
        backgroundColor: '#f9fafb'
      }}>
        <div style={{ opacity: 0.6 }}>
          <span style={{ fontSize: '2rem' }}>⏳</span>
          <h3 style={{ fontFamily: 'Georgia, serif', fontSize: '1.25rem', marginTop: '1rem' }}>
            {t('artworkDetail.proof.title')}
          </h3>
          <p style={{ color: '#6b7280', marginTop: '0.5rem' }}>
            {t('artworkDetail.proof.loading')}
          </p>
        </div>
      </div>
    );
  }

  if (!hasBlockchainAccess) {
    return (
      <div style={{
        padding: '2rem',
        border: '1px solid #e5e7eb',
        borderRadius: '0.5rem',
        textAlign: 'center',
        backgroundColor: '#f9fafb',
        position: 'relative'
      }}>
        <div style={{ opacity: 0.6 }}>
          <span style={{ fontSize: '2rem' }}>🔒</span>
          <h3 style={{ fontFamily: 'Georgia, serif', fontSize: '1.25rem', marginTop: '1rem' }}>
            {t('artworkDetail.proof.title')}
          </h3>
          <p style={{ color: '#6b7280', marginTop: '0.5rem' }}>
            {t('artworkDetail.proof.lockedText')}
          </p>
          <button style={{
            marginTop: '1rem',
            padding: '0.75rem 1.5rem',
            backgroundColor: '#000',
            color: '#fff',
            border: 'none',
            borderRadius: '0.375rem',
            cursor: 'pointer',
            fontWeight: '500'
          }}>
            {t('artworkDetail.proof.upgradeButton')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: '2rem',
      border: '1px solid #e5e7eb',
      borderRadius: '0.5rem',
      backgroundColor: '#fff'
    }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ fontFamily: 'Georgia, serif', fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          🔐 {t('artworkDetail.proof.title')}
        </h3>
        {certificate?.blockchain_status === 'hash_generated' && (
          <span style={{
            display: 'inline-block',
            padding: '0.25rem 0.75rem',
            backgroundColor: '#dbeafe',
            color: '#1e40af',
            borderRadius: '9999px',
            fontSize: '0.75rem',
            fontWeight: '500',
            marginTop: '0.5rem'
          }}>
            {t('artworkDetail.proof.statusPending')}
          </span>
        )}
        {certificate?.blockchain_status === 'blockchain_certified' && (
          <span style={{
            display: 'inline-block',
            padding: '0.25rem 0.75rem',
            backgroundColor: '#d1fae5',
            color: '#065f46',
            borderRadius: '9999px',
            fontSize: '0.75rem',
            fontWeight: '500',
            marginTop: '0.5rem'
          }}>
            ✓ {t('artworkDetail.proof.statusCertified')}
          </span>
        )}
      </div>

      {!certificate && (
        <div>
          <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
            {t('artworkDetail.proof.emptyText')}
          </p>
          <button
            onClick={generateCertificate}
            disabled={loading}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#000',
              color: '#fff',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? t('artworkDetail.proof.generating') : `🔗 ${t('artworkDetail.proof.generateButton')}`}
          </button>
        </div>
      )}

      {certificate && (
        <div>
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ marginBottom: '1rem' }}>
              <span style={{ fontWeight: '500', color: '#374151' }}>{t('artworkDetail.proof.certificateId')}</span>
              <span style={{ marginLeft: '0.5rem', fontFamily: 'monospace', color: '#6b7280' }}>
                {certificate.certificate_id}
              </span>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <span style={{ fontWeight: '500', color: '#374151' }}>{t('artworkDetail.proof.fingerprint')}</span>
              <span style={{ marginLeft: '0.5rem', fontFamily: 'monospace', color: '#6b7280', fontSize: '0.875rem' }}>
                {certificate.certificate_hash.substring(0, 32)}...
              </span>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <span style={{ fontWeight: '500', color: '#374151' }}>{t('artworkDetail.proof.createdAt')}</span>
              <span style={{ marginLeft: '0.5rem', color: '#6b7280' }}>
                {new Date(certificate.timestamp).toLocaleString('fr-FR')}
              </span>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <span style={{ fontWeight: '500', color: '#374151' }}>{t('artworkDetail.proof.technology')}</span>
              <span style={{ marginLeft: '0.5rem' }}>
                <span style={{
                  padding: '0.25rem 0.5rem',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  borderRadius: '0.25rem',
                  fontSize: '0.75rem',
                  fontWeight: '500'
                }}>
                  SHA-256
                </span>
                {certificate.blockchain_status === 'blockchain_certified' ? (
                  <span style={{
                    marginLeft: '0.5rem',
                    padding: '0.25rem 0.5rem',
                    backgroundColor: '#d1fae5',
                    color: '#065f46',
                    borderRadius: '0.25rem',
                    fontSize: '0.75rem',
                    fontWeight: '500'
                  }}>
                    Tezos
                  </span>
                ) : (
                  <span style={{
                    marginLeft: '0.5rem',
                    padding: '0.25rem 0.5rem',
                    backgroundColor: '#fef3c7',
                    color: '#92400e',
                    borderRadius: '0.25rem',
                    fontSize: '0.75rem',
                    fontWeight: '500'
                  }}>
                    {t('artworkDetail.proof.notAnchored')}
                  </span>
                )}
              </span>
            </div>

            {certificate.blockchain_status === 'hash_generated' && (
              <div style={{
                padding: '1rem',
                backgroundColor: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '0.375rem',
                marginTop: '1rem'
              }}>
                <p style={{ fontSize: '0.875rem', color: '#1e40af' }}>
                  ℹ️ {t('artworkDetail.proof.pendingInfo')}
                </p>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
            {certificate?.certificate_pdf_url ? (
              <a
                href={certificate.certificate_pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  color: '#374151',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                📄 {t('artworkDetail.proof.downloadPdf')}
              </a>
            ) : (
              <button
                disabled
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  color: '#9ca3af',
                  cursor: 'not-allowed',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  backgroundColor: '#f9fafb'
                }}
              >
                📄 {t('artworkDetail.proof.pdfSoon')}
              </button>
            )}

            <button
              disabled
              style={{
                padding: '0.5rem 1rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                color: '#9ca3af',
                cursor: 'not-allowed',
                fontSize: '0.875rem',
                fontWeight: '500',
                backgroundColor: '#f9fafb'
              }}
            >
              🔍 {t('artworkDetail.proof.verifySoon')}
            </button>

            {certificate.blockchain_explorer_url && (
              <a
                href={certificate.blockchain_explorer_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#000',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '0.375rem',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                🌐 {t('artworkDetail.proof.viewOnChain')}
              </a>
            )}
          </div>

          <div style={{
            padding: '1.5rem',
            backgroundColor: '#f9fafb',
            borderRadius: '0.5rem'
          }}>
            <h4 style={{ fontFamily: 'Georgia, serif', marginBottom: '1rem', fontSize: '1.125rem' }}>
              {t('artworkDetail.proof.howItWorksTitle')}
            </h4>
            <ul style={{ paddingLeft: '1.5rem', color: '#4b5563', fontSize: '0.875rem', lineHeight: '1.75' }}>
              <li style={{ marginBottom: '0.5rem' }}>
                {t('artworkDetail.proof.how.unique')}
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                {t('artworkDetail.proof.how.tamperproof')}
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                {t('artworkDetail.proof.how.timestamped')}
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                {t('artworkDetail.proof.how.verifiable')}
              </li>
              <li>
                {t('artworkDetail.proof.how.comingSoon')}
              </li>
            </ul>
          </div>
        </div>
      )}

      {error && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '0.375rem',
          color: '#991b1b',
          marginTop: '1rem'
        }}>
          <span style={{ marginRight: '0.5rem' }}>⚠️</span>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default BlockchainCertificate;